import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, BarChart3, ShieldCheck, Wallet, Zap } from 'lucide-react';
import api from '../../lib/api';

/**
 * Pitch para productoras. Bento con CTA + 4 features + stats reales.
 */
export default function ProducerValueProp() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/public/platform-stats');
        if (mounted) setStats(res.data);
      } catch (e) {
        if (mounted) setStats(null);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#050505]"
      data-testid="producer-value-prop"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-6 sm:p-10 lg:p-14">
        {/* Left: text + CTA */}
        <div className="lg:col-span-7 flex flex-col justify-between gap-10">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-6"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              ¿Producís eventos?
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[0.92] tracking-tighter uppercase"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              Creá el
              <br />
              <span className="text-[#D4AF37]">Momento Euge.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/55 leading-relaxed max-w-xl">
              No vendemos tickets, construimos noches que se cuentan. Bravo es la plataforma que entiende cómo se vive la noche en Argentina: liquidaciones en tiempo real, control de accesos con QR, análisis de venta y soporte humano.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/productoras/aplicar"
              className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-extrabold uppercase tracking-[0.18em] text-sm px-6 py-3.5 rounded-full transition-colors"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
              data-testid="producer-signup-cta"
            >
              Registrá tu productora
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/quienes-somos"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium tracking-wide transition-colors"
              data-testid="producer-learn-more"
            >
              Conocé Bravo
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        {/* Right: 2x2 features */}
        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          <Feature
            icon={<BarChart3 size={18} />}
            title="Data en vivo"
            body="Ventas, conversión y velocidad por tanda en tiempo real."
          />
          <Feature
            icon={<Wallet size={18} />}
            title="Liquidaciones claras"
            body="Settlement transparente, sin sorpresas."
          />
          <Feature
            icon={<ShieldCheck size={18} />}
            title="Control de acceso"
            body="QR validados, RRPP propios y staff dedicado."
          />
          <Feature
            icon={<Zap size={18} />}
            title="Crece con vos"
            body="Tandas, descuentos, comunidad y email marketing."
          />
        </div>
      </div>

      {/* Bottom strip with REAL stats */}
      <div className="relative border-t border-white/10 grid grid-cols-3 divide-x divide-white/10">
        <Stat
          number={formatStat(stats?.events_total)}
          label="Eventos en plataforma"
        />
        <Stat
          number={formatStat(stats?.users_total)}
          label="Usuarios activos"
        />
        <Stat
          number={formatStat(stats?.tickets_sold)}
          label="Entradas vendidas"
        />
      </div>
    </div>
  );
}

function formatStat(n) {
  if (n === null || n === undefined) return '—';
  if (typeof n !== 'number') return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace('.0', '')}K+`;
  return String(n);
}

function Feature({ icon, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 hover:border-[#D4AF37]/40 transition-colors"
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D4AF37]/12 mb-4"
        style={{ color: '#D4AF37' }}
      >
        {icon}
      </div>
      <h4
        className="text-sm font-extrabold text-white mb-1 uppercase tracking-wide"
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {title}
      </h4>
      <p className="text-xs text-white/50 leading-relaxed">{body}</p>
    </motion.div>
  );
}

function Stat({ number, label }) {
  return (
    <div className="px-6 py-6 sm:py-8 text-center">
      <p
        className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tighter"
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {number}
      </p>
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/50 font-bold mt-2">
        {label}
      </p>
    </div>
  );
}
